<template>
  <AppPage
    :guide="guide"
    :message="message"
    :workingDialog="busy"
    :tempMessage="info"
    :breadcrumbs="breadcrumbs"
  >
    <v-row fill-height justify-center align-center class="ma-1">
      <v-col cols="12">
        <v-expansion-panels :value="welcomeExpand">
          <v-expansion-panel @change="toggleExpand">
            <v-expansion-panel-header>
              <template v-slot:default="{ open }">
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-fade-transition leave-absolute>
                      <div
                        v-if="!open"
                        key="0"
                        class="text-h6"
                      >
                        Welcome to Asset Risk Analyzer!
                        <v-btn
                          class="float-right"
                          color="primary"
                          text
                          @click.stop="createNewAnalysis"
                        >
                          Create a New Analysis
                        </v-btn>
                        <v-btn
                          class="float-right"
                          color="primary"
                          text
                          @click.stop="openAnalysisList"
                        >
                          Open an Existing Analysis
                        </v-btn>
                      </div>
                      <div
                        v-else
                        key="1"
                        class="text-h6"
                      >
                        Welcome to Asset Risk Analyzer!
                      </div>
                    </v-fade-transition>
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="ma-4 align-center justify-center">
                <WelcomeCard
                  img-src="/getting-started.png"
                  @click="viewGuideMeVideo"
                  img-gradient-start="rgba(188, 245, 214,.33)"
                  img-gradient-end="rgba(60, 179, 113,.7)"
                  title="View getting started video"
                  sub-title="Watch this video to see a quick overview of how to get started using Asset Risk Analyzer."
                  button-text="Watch the video"
                ></WelcomeCard>
                <WelcomeCard
                  img-src="/sample-analysis.png"
                  @click="createSampleAnalysis"
                  img-gradient-start="rgba(188, 245, 214,.33)"
                  img-gradient-end="rgba(60, 179, 113,.7)"
                  title="Get started with a sample"
                  sub-title="Start here and quickly explore the features of Asset Risk Analyzer with a pre-built sample analysis."
                  button-text="Create a Sample Analysis"
                ></WelcomeCard>
                <WelcomeCard
                  img-src="/new-analysis.png"
                  @click="createNewAnalysis"
                  title="Create a new analysis"
                  sub-title="Create a new analysis using your data.  Easily add or import your assets to discover your opportunity."
                  button-text="Create a New Analysis"
                ></WelcomeCard>
                <WelcomeCard
                  img-src="/analysis-list.png"
                  @click="openAnalysisList"
                  title="Open an existing analysis"
                  sub-title="Already have analyses?  Search and manage your existing analysis data here."
                  button-text="Open an Existing Analysis"
                ></WelcomeCard>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <v-row class="ma-1">
      <v-col cols="12" md="4">
        <CountCard
          title="Risk Analyses"
          :loading="loading"
          route="/apps/analyses"
          :icon="riskIcon"
          :value="analysisCount.toLocaleString()"
          :subtitle="`${remainingAnalysisSlots} of ${analysisLimit} remaining`"
          :tooltip="`Your account contains <strong>${analysisCount.toLocaleString()}</strong> analyses.  You may create up to <strong>${analysisLimit}</strong> analyses in your current subscription.<br/><br/>To increase or remove these limits, click the <em class='fa-solid fa-circle-arrow-up accent--text'></em> <strong>UPGRADE</strong> button above to upgrade to HxGN APM.`"
        >
        </CountCard>
      </v-col>
      <v-col cols="12" md="4">
        <CountCard
          title="Assets"
          :loading="aggregateLoading"
          :icon="assetIcon"
          :value="assetCount.toLocaleString()"
          :subtitle="`Maximum of ${assetsPerAnalysis} assets per analysis`"
          :tooltip="`Your account contains <strong>${assetCount.toLocaleString()}</strong> assets.  You may create up to <strong>${assetsPerAnalysis}</strong> assets per analysis in your current subscription.<br/><br/>To increase or remove these limits, click the <em class='fa-solid fa-circle-arrow-up accent--text'></em> <strong>UPGRADE</strong> button above to upgrade to HxGN APM.`"
        >
        </CountCard>
      </v-col>
      <v-col cols="12" md="4">
        <CountCard
          title="Assets for Improvement"
          :loading="aggregateLoading"
          icon="fa-chart-line"
          :value="assetImprovementCount.toLocaleString()"
          :tooltip="`A total of <strong>${assetImprovementCount.toLocaleString()}</strong> assets have been marked for improvement.<br/><br/>To improve these assets with HxGN APM, click the <em class='fa-solid fa-circle-arrow-up accent--text'></em> <strong>UPGRADE</strong> button above to upgrade today.`"
          >
        </CountCard>
      </v-col>
    </v-row>
    <v-row class="ma-1">
      <v-col cols="12">
        <ChartCard
          :themeVariant="1"
          :error="aggregateError"
          :working="aggregateLoading"
          :showLink="false"
          :showSettings="false"
          title="Asset Characteristic Life (MTBF) by Classification"
          :options="mtbfOptions"
        >
        </ChartCard>
      </v-col>
    </v-row>
    <v-row class="ma-1">
      <v-col cols="12" md="6">
        <ChartCard
          :themeVariant="1"
          :error="aggregateError"
          :working="aggregateLoading"
          :showLink="false"
          :showSettings="false"
          title="Top 25 Asset Counts by Classification"
          :options="assetCountOptions"
        >
        </ChartCard>
      </v-col>
      <v-col cols="12" md="6">
        <ChartCard
          :themeVariant="1"
          :error="aggregateError"
          :working="aggregateLoading"
          :showLink="false"
          :showSettings="false"
          title="Top 25 Asset Consequences by Classification"
          :options="consequenceOptions"
        >
        </ChartCard>
      </v-col>
    </v-row>
  </AppPage>
</template>

<script>
import axios from 'axios'
import { mapState, mapGetters } from 'vuex'
import AppPage from "@/components/AppPage"
import WelcomeCard from "@/components/WelcomeCard"
import CountCard from "@/components/dashboard/CountCard"
import { ExceptionMessage, ASSET_ICON, ChartCard, RISK_ICON } from '@itus/ui.common'
import sampleAnalysis from "@/views/apps/analyses/sampleAnalysis.json"
import { DateTime, Duration } from 'luxon'
import { v4 as uuidv4 } from 'uuid'
import { buildApiUrl } from '@/components/api-url-builder'
import { AggregateHelper } from "@/components/dashboard/AggregateHelper"
import Guide from "@/guides/home.json"

export default {
  name: 'Home',
  components: {
    AppPage,
    WelcomeCard,
    CountCard,
    ChartCard
  },
  data() {
    return {
      assetIcon: ASSET_ICON,
      riskIcon: RISK_ICON,
      loading: true,
      aggregateLoading: true,
      busy: { show: false },
      guide: Guide,
      message : { show: false, type: 'error', text: '' },
      info: { type: 'info', show: false, text: '' },
      breadcrumbs: [
        { id: 'home', title: 'Home', icon:'fa-solid fa-house', url: '/home' }
      ],
      sampleAnalysis: sampleAnalysis,
      welcomeExpand: -1,
      aggregateHelper: null,
      assetCountOptions: null,
      mtbfOptions: null,
      consequenceOptions: null,
      aggregateError: null
    }
  },
  computed: {
    ...mapState({
      user: state => state.user,
      analyses: state => state.analyses.analyses,
      homeExpand: state => state.preferences.homeExpand
    }),
    ...mapGetters({
      analysisLimit: 'analyses/analysisLimit',
      assetsPerAnalysis: 'analyses/assetsPerAnalysis'
    }),
    hasAnalyses() {
      return this.analysisCount > 0
    },
    aggregateData() {
      return !this.aggregateHelper || !this.aggregateHelper.Data ? null : this.aggregateHelper.Data
    },
    assetCount() {
      return this.aggregateData && this.aggregateData.totalCount ? this.aggregateData.totalCount : 0
    },
    assetImprovementCount() {
      return this.aggregateData && this.aggregateData.totalImproveCount ? this.aggregateData.totalImproveCount : 0
    },
    analysisCount() {
      return (this.analyses?.length) ? this.analyses.length : 0
    },
    remainingAnalysisSlots() {
      return this.analysisLimit - this.analysisCount
    }
  },
  methods : {
    toggleExpand() {
      if(this.welcomeExpand === 0) {
        this.welcomeExpand = -1
        this.$store.dispatch('preferences/updateHomeExpand', false)
      } else {
        this.welcomeExpand = 0
        this.$store.dispatch('preferences/updateHomeExpand', true)
      }
    },
    loadAnalyses() {
      this.loading = true
      this.$store.dispatch('analyses/loadAnalyses', { filters: [] })
        .catch(err => this.showException(err, "Error loading analyses."))
        .finally(() => this.loading = false)

    },
    showException: function(err, defaultMsg) {
      this.message = ExceptionMessage.generateMessage(err, defaultMsg);
    },
    openAnalysisList() {
      this.$router.push('/apps/analyses')
    },
    createNewAnalysis() {
      if (this.analyses.length >= this.analysisLimit) {
        this.showException(null, `You have reached your maximum limit of ${this.analysisLimit} analyses at this tier - you can easily upgrade to get more!`)
        return
      }
      this.$router.push('/apps/analyses/new')
    },
    aggregateOptionsChanged(val) {
      if(!val)
        return

      if(val.item === 'consequence')
        this.consequenceOptions = val.options
      else if(val.item === 'mtbf')
        this.mtbfOptions = val.options
      else if(val.item === 'assetCount')
        this.assetCountOptions = val.options
    },
    aggregateErrorChanged(err, msg) {
      this.aggregateError = err
      this.showException(err, msg ?? 'Error loading aggregate data.')
    },
    aggregateWorkingChanged(val) {
      this.aggregateLoading = val
    },
    async loadAggregateData() {
      this.aggregateHelper = new AggregateHelper(this.aggregateOptionsChanged, this.aggregateErrorChanged, this.aggregateWorkingChanged)
      this.aggregateHelper.ExecuteRequest()
    },
    viewGuideMeVideo() {
      if (!this.$root || !this.$root.$children || !this.$root.$children.length) {
        return
      }
      const app = this.$root.$children.find(x => typeof x.guideClick === 'function')
      if (!app) {
        return
      }
      app.guideClick()
    },
    async createSampleAnalysis() {
      if (this.analyses.length >= this.analysisLimit) {
        this.showException(null, `You have reached your maximum limit of ${this.analysisLimit} analyses at this tier - you can easily upgrade to get more!`)
        return
      }
      try {
        let clonedSample = JSON.parse(JSON.stringify(this.sampleAnalysis))
        clonedSample.analysis.analysis = uuidv4()
        let assets = []
        for(let sampleAsset of clonedSample.assets) {
          sampleAsset.analysisId = clonedSample.analysis.analysis
          sampleAsset.assetId = uuidv4()
          assets.push(sampleAsset)
        }

        let start = DateTime.local().set({hour: 0, minute: 0, second: 0, milliseconds: 0})
        clonedSample.analysis.startDate = start.toISO({ suppressMilliseconds: true, includeOffset: false})
        clonedSample.analysis.endDate = start.plus(Duration.fromISO(clonedSample.analysis.missionTime)).set({hour: 0, minute: 0, second: 0, milliseconds: 0}).toISO({ suppressMilliseconds: true, includeOffset: false})
        this.busy.show = true
        await axios.post(buildApiUrl('/api/analyses/dataload'), [{ analysis: clonedSample.analysis, assets: assets }])
        this.$store.dispatch('analyses/loadAnalyses', { force: true })
        this.$router.push('/apps/analyses/' + clonedSample.analysis.analysis + '?force=true')
        this.$gtag.event('sample_analysis', { 'analysis_id': clonedSample.analysis.analysis })
      } catch(err) {
        this.showException(err, 'Error loading sample analysis.')
      } finally {
        this.busy.show = false
      }

    }
  },
  created() {
    this.$store.dispatch('preferences/loadHomeExpand')
      .then(() => {
        this.welcomeExpand = this.homeExpand && this.homeExpand.preferenceValue == 'false' ? -1 : 0
      })
    this.loadAnalyses()
    this.loadAggregateData()    
    this.$store.dispatch('taxonomy/loadTaxonomy')
  },
  beforeRouteUpdate (to, from, next) {
    this.$children[0].beforeRouteLeave(to, from, next)
  },
  beforeRouteLeave (to, from, next) {
    this.$children[0].beforeRouteLeave(to, from, next)
  }
}
</script>
<style scoped>
.big-number-card {
  height:200px;
}
</style>
